import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { forwardRef, useState, type ButtonHTMLAttributes } from 'react';
import { CalendarIcon } from '../../../icons/calendar';
import { cn } from '../../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';

export interface MonthPickerProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'value' | 'onChange'
> {
  /** ISO month `YYYY-MM`. Empty string when not set. */
  value: string;
  onChange: (value: string) => void;
  hasError?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** First selectable year (inclusive). Default: current year - 90. */
  minYear?: number;
  /** Last selectable year (inclusive). Default: current year + 7. */
  maxYear?: number;
  /**
   * Formats the selected `YYYY-MM` shown in the trigger.
   * Default: `formatMonthYearPtBr` → "Dezembro de 2026" (Figma "Mês de AAAA").
   */
  formatValue?: (value: string) => string;
}

function parseIsoMonth(iso: string): { year: number; month: number } | undefined {
  if (!iso) return undefined;
  const m = /^(\d{4})-(\d{2})$/.exec(iso);
  if (!m) return undefined;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!year || !month || month < 1 || month > 12) return undefined;
  return { year, month };
}

function formatIsoMonth(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * `YYYY-MM` → "Dezembro de 2026" (pt-BR, inicial maiúscula). Mesmo formato
 * "Mês de AAAA" do documento/lista. Valor inválido ou vazio → `''`.
 */
export function formatMonthYearPtBr(iso: string): string {
  const parsed = parseIsoMonth(iso);
  if (!parsed) return '';
  const text = format(new Date(parsed.year, parsed.month - 1, 1), "MMMM 'de' yyyy", {
    locale: ptBR,
  });
  return text.charAt(0).toLocaleUpperCase('pt-BR') + text.slice(1);
}

const MONTH_LABELS = Array.from({ length: 12 }, (_, i) =>
  format(new Date(2000, i, 1), 'LLL', { locale: ptBR })
);

/**
 * MonthPicker — input-like trigger + Popover with a year dropdown + month grid.
 * Returns `YYYY-MM`. Used wherever the form only needs month + year (e.g. graduation date).
 */
export const MonthPicker = forwardRef<HTMLButtonElement, MonthPickerProps>(
  (
    {
      value,
      onChange,
      hasError = false,
      placeholder = 'Selecione mês e ano',
      disabled = false,
      minYear,
      maxYear,
      formatValue = formatMonthYearPtBr,
      className,
      ...rest
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const parsed = parseIsoMonth(value);
    const currentYear = new Date().getFullYear();
    const lo = minYear ?? currentYear - 90;
    const hi = maxYear ?? currentYear + 7;
    const [viewYear, setViewYear] = useState<number>(parsed?.year ?? currentYear);

    const display = parsed ? formatValue(value) : placeholder;

    const years: number[] = [];
    for (let y = hi; y >= lo; y--) years.push(y);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            type="button"
            data-slot="month-picker-trigger"
            disabled={disabled}
            aria-label={rest['aria-label'] ?? 'Abrir seletor de mês e ano'}
            className={cn(
              'flex h-9 w-full cursor-pointer flex-row items-center gap-2 rounded-md border bg-background px-3 text-left text-sm shadow-2xs transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1',
              hasError
                ? 'border-destructive focus-visible:outline-destructive'
                : 'border-input focus-visible:outline-primary',
              disabled && 'cursor-not-allowed opacity-50',
              className
            )}
            {...rest}
          >
            <CalendarIcon className="size-4 shrink-0 text-foreground" />
            <span
              className={cn(
                'flex-1 truncate',
                // Sem `capitalize` CSS: ele title-casearia "de" ("Dezembro De
                // 2026"). A inicial maiúscula vem do `formatValue`.
                parsed ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {display}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="start">
          <div className="flex flex-col gap-3">
            <select
              value={viewYear}
              onChange={(e) => setViewYear(Number(e.target.value))}
              className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              aria-label="Ano"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <div className="grid grid-cols-3 gap-1.5">
              {MONTH_LABELS.map((label, i) => {
                const month = i + 1;
                const isSelected =
                  parsed !== undefined && parsed.year === viewYear && parsed.month === month;
                return (
                  <button
                    key={month}
                    type="button"
                    onClick={() => {
                      onChange(formatIsoMonth(viewYear, month));
                      setOpen(false);
                    }}
                    className={cn(
                      'h-9 rounded-md px-2 text-sm capitalize transition-colors',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary',
                      isSelected
                        ? 'bg-primary font-medium text-primary-foreground hover:bg-primary'
                        : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

MonthPicker.displayName = 'MonthPicker';
